function normalizeIdentity(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/**
 * Existing/manual values always win. Incoming data may only fill keys that are
 * absent. Empty arrays, "", 0, false and null are intentional edits and are preserved.
 */
export function fillMissingDeep<T>(existing: T, incoming: unknown): T {
  if (!isPlainObject(existing) || !isPlainObject(incoming)) return existing;
  const output: Record<string, unknown> = structuredClone(existing as any);
  for (const [key, incomingValue] of Object.entries(incoming)) {
    if (!(key in output)) {
      output[key] = structuredClone(incomingValue);
      continue;
    }
    if (isPlainObject(output[key]) && isPlainObject(incomingValue)) {
      output[key] = fillMissingDeep(output[key] as any, incomingValue);
    }
  }
  return output as T;
}

function identity(row: any) {
  return normalizeIdentity(row?.normalized_key ?? row?.name ?? row?.canonical_name ?? row?.generic_name);
}

export function mergeKnowledgePreservingEdits<T extends Record<string, any>>(existing: T[], incoming: T[]): T[] {
  const map = new Map<string, T>();
  for (const row of existing) {
    const key = identity(row);
    if (key) map.set(key, structuredClone(row));
  }
  for (const row of incoming) {
    const key = identity(row);
    if (!key) continue;
    const current = map.get(key);
    map.set(key, current ? fillMissingDeep(current, row) : structuredClone(row));
  }
  return [...map.values()];
}

export function assertKnowledgeData(payload: any) {
  if (payload?.sqlite_path && !Array.isArray(payload?.templates) && !Array.isArray(payload?.substances)) {
    throw new Error(
      `Este arquivo é somente o manifesto da base (${payload.substance_count ?? "?"} substâncias). Envie o arquivo ${payload.sqlite_path} ou uma exportação JSON das fichas para atualizar o conteúdo.`,
    );
  }
  if (!Array.isArray(payload) && !Array.isArray(payload?.templates) && !Array.isArray(payload?.substances)) {
    throw new Error("Formato de conhecimento não reconhecido.");
  }
}
